<template>

    <div class="px-6 py-4 bg-minplan-beige -mb-4">
        <page-title :title="$t('documents.new')"></page-title>
    </div>

    <jet-form-section @submitted="createDocument()">

        <template #form>

            <form-card :title="$t('documents.document')" :description="$t('documents.documentDescription')">

                <div class="col-span-6">
                    <label for="name" class="block text-sm font-medium text-gray-700">{{ $t("documents.name") }} <RequiredMark /></label>
                    <input type="text" name="name" id="name" v-model="form.name"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                           autofocus />
                    <jet-input-error :message="form.errors.name" class="mt-2" />
                </div>

                <div class="col-span-6" v-if="$page.props.document_digital_sign_mode === 'On'">
                    <label class="block text-sm font-medium text-gray-700">Digitale Signatur benötigt?</label>
                    <div class="mt-1 flex items-center space-x-4">
                        <label class="inline-flex items-center"><input type="radio" value="Yes" v-model="form.digital_signature" /> <span class="ml-2">Ja</span></label>
                        <label class="inline-flex items-center ml-4"><input type="radio" value="No" v-model="form.digital_signature" /> <span class="ml-2">Nein</span></label>
                    </div>
                </div>

                <div class="col-span-6">

                    <label class="block text-sm font-medium text-gray-700">
                        {{ $t("documents.file") }} <RequiredMark />
                    </label>
                    <FileDropzone
                        :accept="form.digital_signature === 'Yes' ? '.pdf' : allowedMimesAccept"
                        @file="onDropFile"
                        @reject="onFileRejected"
                    >
                        <div class="mt-1 flex items-center">

                            <div class="mt-2 w-full" v-show="fileUploaded">
                                <span class="block">{{ fileUploaded }}</span>
                            </div>

                            <jet-secondary-button class="mt-2 mr-2" type="button" @click.prevent="selectNewFile"
                                                  v-if="!fileUploaded">{{ $t("actions.chooseFile") }}
                            </jet-secondary-button>
                            <jet-secondary-button class="mt-2 mr-2" type="button" @click.prevent="clearFileInput"
                                                  v-if="fileUploaded">{{ $t("actions.removeFile") }}
                            </jet-secondary-button>

                            <input type="file" class="hidden" ref="file" :accept="form.digital_signature === 'Yes' ? '.pdf' : allowedMimesAccept"
                                   @change="updateFileUploaded" />
                        </div>
                    </FileDropzone>
                    <p class="my-3 text-sm text-gray-400">
                        <span v-if="form.digital_signature === 'Yes'">PDF (max. {{ signedMaxSizeInMb }} MB)</span>
                        <span v-else>{{ $t("documents.fileInfo", { size: maxSizeInMb, extensions: documentMimes.toUpperCase().replace(/,/g, ' / ') }) }}</span>
                    </p>
                    <jet-input-error :message="form.errors.file" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <label for="comment" class="block text-sm font-medium text-gray-700">{{
                            $t("documents.comment")
                        }}</label>
                    <div class="flex">
                        <textarea type="textarea" name="comment" id="comment" v-model="form.comment"
                                  class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    </div>
                    <jet-input-error :message="form.errors.comment" class="mt-2" />
                </div>

            </form-card>

        </template>

        <template #actions>
            <div class="flex justify-end my-4 mx-6">

                <button @click="cancel" type="button" class="inline-block uppercase font-semibold text-xs tracking-widest">
                    {{ $t("actions.cancel") }}
                </button>

                <primary-button type="button" @click="handleSave" :class="{ 'opacity-25': form.processing }" :disabled="form.processing" class="ml-3 ">
                    <span v-if="form.processing" class="flex items-center gap-1">
                        <svg class="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                        Saving...
                    </span>
                    <span v-else>{{ $t("actions.save") }}</span>
                </primary-button>

            </div>
        </template>

    </jet-form-section>

    <teleport to="body">
        <digital-signature-config 
            v-if="showSignConfigModal"
            class="digital-signature-config-modal-root"
            :buyers="buyers"
            :pdf-base64="pdfBase64"
            :file-name="form.name || fileUploaded"
            :auth-user-id="$page.props.user.id"
            @cancel="showSignConfigModal = false"
            @done="finalizeDigitalSignature"
        />
    </teleport>

</template>


<script>
import { defineComponent, ref } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetFormSection from "@/Jetstream/FormSection";
import JetInput from "@/Jetstream/Input";
import JetInputError from "@/Jetstream/InputError";
import JetLabel from "@/Jetstream/Label";
import PageTitle from "@/Shared/Components/PageTitle";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import FormCard from "@/Shared/Components/FormCard";
import RequiredMark from "@/Shared/Components/RequiredMark";
import axios from "axios";
import DigitalSignatureConfig from "@/Shared/Components/Documents/Modals/DigitalSignatureConfig.vue";
import FileDropzone from "@/Shared/Components/FileDropzone.vue";
import documentFileField from "@/Shared/Mixins/documentFileField.js";

// `documentFileField`-Mixin (R3 Punkt 1, BACKLOG §5 Welle-4 (g)):
// `maxSizeInMb`/`documentMimes`/`allowedMimesAccept` + Auswahl-/Entfernen-/
// Drop-Handling kommen von dort (vorher ~70 Zeilen Kopie gegenüber
// `Pages/Projects/Modals/DistributeDocument.vue`). `setFile()` überschrieben,
// weil die Datei hier in `form.file` (`useForm`) landet, nicht in `this.file`.
// Die Grösse-/Endung-Prüfung läuft dadurch neu bereits BEI DER AUSWAHL statt
// erst in `handleSave()` — bewusste Verhaltensangleichung an
// `DistributeDocument.vue`, s. `handleSave()` unten.
export default defineComponent({
    props: {
        storeLink: String,
        buyers: { type: Array, default: () => [] },
        auth_user_id: [String, Number],
        // Hotfix 08.10.2026: Grenze fuer zu signierende Dateien als
        // Seiten-Prop (`core.max_signed_offer_upload_size`, dieselbe Quelle
        // wie bei den Offerten).
        max_signed_document_upload_size: Number,
    },
    inheritAttrs: false,
    layout: MainLayout,
    emits: ["closeModal"],
    mixins: [documentFileField],
    components: {
        PageTitle,
        PrimaryButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        FormCard,
        RequiredMark,
        DigitalSignatureConfig,
        FileDropzone,
    },

    computed: {
        // Der Server prueft diese Grenze an den Signatur-Endpunkten
        // (`DigitalSignatureController::assertSignableSize`); hier ist sie die
        // fruehere Rueckmeldung — vor der Signatur-Konfiguration statt nach
        // ihr. Fallback = Server-Default.
        signedMaxBytes() {
            return this.max_signed_document_upload_size || 10 * 1024 * 1024;
        },
        signedMaxSizeInMb() {
            return Math.round(this.signedMaxBytes / 1024 / 1024);
        },
        selectionStyle() {
            const sx = Math.min(this.startPt.x, this.currentPt.x);
            const sy = Math.min(this.startPt.y, this.currentPt.y);
            const sw = Math.abs(this.startPt.x - this.currentPt.x);
            const sh = Math.abs(this.startPt.y - this.currentPt.y);
            return {
                position: 'absolute',
                left: sx + 'px',
                top: sy + 'px',
                width: sw + 'px',
                height: sh + 'px',
                border: '2px solid #f00',
                background: 'rgba(255,0,0,0.1)',
                boxSizing: 'border-box'
            };
        }
    },
    methods: {
        cancel() {
            this.$emit("closeModal");
        },
        // Mixin-Override (R3 Punkt 1): die Datei landet hier in `form.file`
        // (`useForm`), nicht in `this.file` (Mixin-Default).
        setFile(file) {
            this.form.file = file;
        },
        handleSave() {
            if (!this.form.name) {
                this.form.errors.name = "Name is required.";
                return;
            }

            // Grösse/Endung sind bereits bei der Auswahl geprüft
            // (`documentFileField`-Mixin, `updateFileUploaded()`) — hier nur
            // noch das Signatur-Spezifische (PDF-Pflicht, engere Grössengrenze),
            // das dort bewusst NICHT geprüft wird (es gilt nur für diesen Host).
            if (this.form.file) {
                const fileExt = this.form.file.name.split('.').pop().toLowerCase();

                if (this.form.digital_signature === 'Yes' && fileExt !== 'pdf') {
                    this.$notify({ group: "error", title: "actions.error", text: `Digital signature requires PDF.` });
                    return;
                }

                // Erst hier und nicht bei der Auswahl: die Reihenfolge
                // «Datei, dann Ja» muss genauso greifen wie «Ja, dann Datei».
                if (this.form.digital_signature === 'Yes' && this.form.file.size > this.signedMaxBytes) {
                    this.$notify({
                        group: "error",
                        title: "actions.error",
                        text: this.$t("documents.signedFileTooBig", { size: this.signedMaxSizeInMb }),
                    });
                    return;
                }

                if (this.form.digital_signature === 'Yes') {
                    this.loadPdfBase64();
                    this.showSignConfigModal = true;
                    return;
                }
            } else if (this.form.digital_signature === 'Yes') {
                this.form.digital_signature = 'No';
            }

            this.createDocument();
        },

        createDocument() {
            this.form.post(this.storeLink, {
                preserveScroll: true,
                onSuccess: () => this.$notify({
                    group: "success",
                    title: "actions.success",
                    text: "documents.created"
                })
            });
        },
        
        // N8 (Fix-Runde 1): `form.file` statt `$refs.file.files[0]` — seit
        // dem `documentFileField`-Mixin ist `form.file` (via `setFile()`)
        // die EINE Wahrheit für die ausgewählte Datei, der native Input
        // dient nur noch der Auswahl-UI.
        loadPdfBase64() {
            const file = this.form.file;
            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl = reader.result;
                if (typeof dataUrl === 'string') {
                    this.pdfBase64 = dataUrl.split(',')[1];
                    this.pdfLoaded = true;
                }
            };
            reader.readAsDataURL(file);
        },

        b64toFile(b64Data, contentType='', sliceSize=512) {
            const byteCharacters = atob(b64Data);
            const byteArrays = [];
            for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
                const slice = byteCharacters.slice(offset, offset + sliceSize);
                const byteNumbers = new Array(slice.length);
                for (let i = 0; i < slice.length; i++) byteNumbers[i] = slice.charCodeAt(i);
                byteArrays.push(new Uint8Array(byteNumbers));
            }
            return new File(byteArrays, this.form.name + ".pdf", { type: contentType });
        },

        finalizeDigitalSignature(payload) {
            this.showSignConfigModal = false;
            this.form.digital_sign_json = payload.digital_sign_json;
            if (payload.pdf_base64_signed) {
                this.form.file = this.b64toFile(payload.pdf_base64_signed, 'application/pdf');
            }
            this.createDocument();
        },
    },
    setup(props) {
        const fileUploaded = ref(null);
        const { props: pageProps } = usePage();
        const dsMode = pageProps.value.document_digital_sign_mode;

        const form = useForm({
            name: null,
            comment: null,
            file: null,
            digital_signature: "No",
            digital_sign_json: null
        });

        return { form, fileUploaded };
    },
    data() {
        return {
            showSignConfigModal: false,
            pdfLoaded: false,
            pdfBase64: null,
        }
    }
});
</script>

<style scoped></style>
